import { Box, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import {
  ProductInView,
  priceRangeLabel,
} from '../../../utils/catalog/catalogFilters';
import { formatDollarAmount } from '../../../utils/global/helper';

interface Props {
  products: ProductInView[];
  selectedProductId: string | null;
  onSelect: (entry: ProductInView) => void;
  /** The pane that scrolls; rows outside it are not rendered */
  scrollParent: HTMLElement | null;
}

interface RowProps {
  entry: ProductInView;
  selected: boolean;
  onSelect: (entry: ProductInView) => void;
}

const productKey = (_: number, entry: ProductInView) => entry.product.id;

function ProductRow({ entry, selected, onSelect }: RowProps) {
  const theme = useTheme();
  const { product } = entry;
  return (
    <ButtonBase
      onClick={() => onSelect(entry)}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.75,
        borderRadius: '10px',
        textAlign: 'left',
        fontFamily: theme.typography.fontFamily,
        bgcolor: selected ? theme.palette.accent.main : 'transparent',
        '&:hover': { bgcolor: theme.palette.accent.main },
      }}>
      <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '8px',
            bgcolor: theme.palette.lightGrey.main,
            overflow: 'hidden',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {product.coverImageUrl ? (
            <Box
              component="img"
              src={product.coverImageUrl}
              alt={product.name}
              loading="lazy"
              decoding="async"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Typography variant="h5" color="text.secondary">
              {product.name.slice(0, 2)}
            </Typography>
          )}
        </Box>
        <Box minWidth={0}>
          <Typography variant="subtitle2" noWrap>
            {product.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[
              product.brand?.name,
              `${entry.variants.length} option${
                entry.variants.length === 1 ? '' : 's'
              }`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
      </Stack>
      <Typography variant="body2" color="text.secondary" flexShrink={0}>
        {priceRangeLabel(entry.variants, amount => formatDollarAmount(amount))}
      </Typography>
    </ButtonBase>
  );
}

export default function ProductList({
  products,
  selectedProductId,
  onSelect,
  scrollParent,
}: Props) {
  if (!products.length) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        py={3}>
        Nothing matches these filters.
      </Typography>
    );
  }
  if (!scrollParent) return null;

  const productRow = (_: number, entry: ProductInView) => (
    <Box pb={0.5}>
      <ProductRow
        entry={entry}
        selected={entry.product.id === selectedProductId}
        onSelect={onSelect}
      />
    </Box>
  );

  return (
    <Virtuoso
      customScrollParent={scrollParent}
      data={products}
      computeItemKey={productKey}
      itemContent={productRow}
    />
  );
}
